import { useEffect, useState } from 'react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useFirebaseProjectsHooks } from './projectHooks';

const Projects = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [activeFilter, setActiveFilter] = useState('all');
    const { t } = useLanguage();
    const { language } = useLanguage();
    const { projects: { categories, content } } = useFirebaseProjectsHooks(language);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 },
        );

        const element = document.getElementById('projects');
        if (element) {
            observer.observe(element);
        }

        return () => {
            if (element) {
                observer.unobserve(element);
            }
        };
    }, []);

    const filteredProjects =
        activeFilter === 'all'
            ? Object.values(content ?? {})
            : Object.values(content ?? {}).filter(
                  (project: any) => project.category === activeFilter,
              );

    return (
        <section id="projects" className="bg-ink py-24">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div
                    className={`transition-all duration-700 ${
                        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                    }`}
                >
                    <div className="mb-10 max-w-3xl">
                        <h2 className="mb-4 font-display text-4xl font-bold tracking-tight text-fg md:text-5xl">
                            {t('projects.title')}{' '}
                            <span className="text-accent">{t('projects.titleHighlight')}</span>
                        </h2>
                        <p className="mb-8 text-lg text-muted md:text-xl">{t('projects.subtitle')}</p>

                        <div className="flex flex-wrap gap-x-6 gap-y-2 border-b border-white/10">
                            {Array.from(categories ?? []).map((filter: any) => (
                                <button
                                    key={filter}
                                    type="button"
                                    onClick={() => setActiveFilter(filter)}
                                    className={`relative pb-3 text-sm font-semibold capitalize transition-colors ${
                                        activeFilter === filter
                                            ? 'text-accent'
                                            : 'text-muted hover:text-fg'
                                    }`}
                                >
                                    {String(filter)}
                                    <span
                                        className={`absolute bottom-0 left-0 h-0.5 bg-accent transition-all ${
                                            activeFilter === filter ? 'w-full' : 'w-0'
                                        }`}
                                        aria-hidden="true"
                                    />
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {filteredProjects.map((project: any) => {
                            const techs = Array.from(project.technologies ?? []);

                            return (
                                <article
                                    key={project.id}
                                    className="group flex flex-col overflow-hidden border border-white/10 bg-surface transition-colors duration-300 hover:border-accent/40"
                                >
                                    <a
                                        href={project.demoUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="relative block aspect-[16/9] overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                    >
                                        <img
                                            src={project.image}
                                            alt={project.title}
                                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                                        />
                                        <div
                                            className="absolute inset-0 bg-gradient-to-t from-ink/55 via-transparent to-transparent"
                                            aria-hidden="true"
                                        />
                                        {project.featured && (
                                            <span className="absolute left-2.5 top-2.5 bg-highlight px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ink">
                                                {t('common.featured')}
                                            </span>
                                        )}
                                    </a>

                                    <div className="flex flex-1 flex-col gap-2.5 p-4">
                                        <h3 className="font-display text-base font-semibold leading-snug text-fg transition-colors group-hover:text-accent">
                                            {project.title}
                                        </h3>
                                        <p className="line-clamp-2 text-sm leading-relaxed text-muted">
                                            {project.description}
                                        </p>

                                        <div className="flex flex-wrap gap-1.5">
                                            {techs.slice(0, 4).map((tech: any, techIndex: number) => (
                                                <span
                                                    key={techIndex}
                                                    className="border border-white/10 px-2 py-0.5 text-[11px] text-muted"
                                                >
                                                    {tech}
                                                </span>
                                            ))}
                                            {techs.length > 4 && (
                                                <span className="px-1.5 py-0.5 text-[11px] text-muted/70">
                                                    +{techs.length - 4}
                                                </span>
                                            )}
                                        </div>

                                        <div className="mt-auto flex items-center gap-4 pt-1">
                                            <a
                                                href={project.demoUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-xs font-medium text-accent hover:text-accent-soft"
                                            >
                                                {t('projects.actions.demo')}
                                            </a>
                                            {project.githubUrl && (
                                                <a
                                                    href={project.githubUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-xs font-medium text-muted hover:text-fg"
                                                >
                                                    {t('projects.actions.code')}
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </article>
                            );
                        })}
                    </div>

                    <div className="mt-14 flex flex-wrap items-center gap-4">
                        <p className="text-muted">{t('projects.cta.text')}</p>
                        <a
                            href="#contact"
                            className="inline-flex items-center bg-accent px-6 py-3 font-semibold text-fg transition-colors hover:bg-accent-soft"
                        >
                            {t('projects.cta.button')}
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
};

export { Projects };
